import * as Sentry from '@sentry/react-native'
import { recordCrashlyticsError } from '@/src/common/hooks/useLogError/recordCrashlyticsError'
import { useEvent } from 'tamagui'
import { stringifyMessage } from './stringifyMessage'
import { useUserStore } from '../../store/useUserStore'
import { environment } from '@/src/common/environment'
import { ErrorLike } from '@/src/clients/sentry/store/listeners/captureQueryFnErrors'

/** @see {@link https://github.com/Acre-Rewards/acre-rewards-api/blob/2947415ef01d1866b112203741a1ba8b06dd9a7d/src/common/logger/redacting-logger.ts#L102-L107} */
function buildBreadcrumbData(optionalParams: string[]) {
  return optionalParams.reduce<NonNullable<Sentry.Event['extra']>>((acc, param, index) => {
    acc[`param-${index}`] = param
    return acc
  }, {})
}

type LogErrorParams = [unknown, ...unknown[]]

export const sentryCaptureException =
  environment === 'local'
    ? (...params: LogErrorParams) => {
        // eslint-disable-next-line no-console
        console.error(
          '[Sentry (disabled)]',
          ...params.map((param) => (typeof param === 'string' ? param : stringifyMessage(param))),
        )
      }
    : (...params: LogErrorParams) => {
        Sentry.captureException(
          params.length === 1
            ? ErrorLike.safeParse(params[0]).success
              ? params[0]
              : new Error(typeof params[0] === 'string' ? params[0] : stringifyMessage(params[0]))
            : new Error(stringifyMessage(params), {
                cause:
                  params[0] instanceof Error
                    ? params[0]
                    : new Error(
                        ErrorLike.safeParse(params[0]).success
                          ? ErrorLike.parse(params[0]).message
                          : typeof params[0] === 'string'
                            ? params[0]
                            : stringifyMessage(params[0]),
                      ),
              }),
          { extra: buildBreadcrumbData(params.map(stringifyMessage)) },
        )
      }

export function logErrorForUser(userId: string | undefined, ...params: LogErrorParams) {
  sentryCaptureException(...params)

  void recordCrashlyticsError(
    userId,
    params.length === 1
      ? params[0] instanceof Error
        ? params[0]
        : new Error(typeof params[0] === 'string' ? params[0] : stringifyMessage(params[0]))
      : new Error(stringifyMessage(params)),
  )
}

export function useLogError() {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { userInformation } = useUserStore()
  return useEvent((...params: LogErrorParams) => logErrorForUser(userInformation?.id, ...params))
}
